import React from "react"
import SEO from '@/components/SEO'
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"

export default function About() {
  return (
    <div className="bg-white pt-32">
      <SEO
        title="About Mehta Associates — Property Valuers Bhavnagar"
        description="Mehta Associates is a government registered & approved property valuation firm in Bhavnagar, Gujarat. Expert valuer Ajay Mehta provides bank-approved valuation reports."
        pathname="/about"
        keywords={["Mehta Associates", "Property Valuer Bhavnagar", "Approved Valuer Gujarat"]}
      />
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero */}
        <header className="text-center mb-12">
          <h1 className="text-3xl font-semibold text-blue-700">About Mehta Associates</h1>
          <p className="mt-2 text-muted-foreground">Government Registered & Approved Property Valuer in Bhavnagar, Gujarat</p>
        </header>

        {/* About Firm */}
        <Card className="rounded-xl shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-semibold text-blue-700">About Mehta Associates</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Mehta Associates is a Government Registered & Approved Real Estate Appraisal firm based in Bhavnagar, Gujarat, led by Mr. Ajay Champaklal Mehta, an experienced and certified valuer specializing in immovable property and agricultural land valuation.
            </p>
            <p className="mt-3 text-muted-foreground">
              Established as a proprietary firm, Mehta Associates provides professional valuation services across Gujarat and Maharashtra, serving banks, financial institutions, government departments, corporate entities, and private clients. With a strong foundation in technical expertise, regulatory compliance, and professional ethics, the firm ensures accurate, independent, and transparent valuation reports in accordance with banking and statutory standards.
            </p>
            <p className="mt-3 text-muted-foreground">
              Keywords: Property Valuer in Bhavnagar, Government Approved Valuer Gujarat, Real Estate Appraiser Bhavnagar.
            </p>
          </CardContent>
        </Card>

        <Separator className="my-8" />

        {/* Proprietor */}
        <Card className="rounded-xl shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-semibold text-blue-700">About the Proprietor – Ajay Champaklal Mehta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-full md:w-1/3 flex justify-center md:justify-start">
                <img
                  src="/AjayUncle.jpeg"
                  alt="Ajay Champaklal Mehta"
                  className="rounded-lg shadow-md object-cover w-48 h-48 md:w-64 md:h-64"
                />
              </div>
              <div className="md:flex-1 space-y-3">
                <p className="text-muted-foreground">Mr. Ajay Champaklal Mehta is a Govt. Registered & Approved Valuer with over 15+ years of experience in property valuation.</p>
                <p className="text-muted-foreground">Professional Qualifications: Master of Commerce – Valuation of Real Estate & Agriculture Land (Gujarat University – March 2019)</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className="">Registration No: CAT–1/756/2022-23</Badge>
                  <Badge className="">Member – Indian Institute of Valuers</Badge>
                  <Badge className="">CAT-I-A-5765</Badge>
                  <Badge className="">CAT-II-A-6605</Badge>
                  <Badge className="">Council of Engineers & Valuers (CEV/SM/1874/MV RE)</Badge>
                  <Badge className="">Registered under Wealth Tax Act u/s 34AB</Badge>
                  <Badge className="">PAN: ALKPM4699L</Badge>
                </div>
                <p className="text-muted-foreground">He previously worked as an Assistant Valuer at S.J. Asset Appraisal Pvt. Ltd. (April 2018 – March 2022), a firm empanelled with major banks. Currently, he is practicing independently as Mehta Associates for the past 15 years.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Separator className="my-8" />

        {/* Banking & Institutional Experience */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-blue-700 mb-4">Banking & Institutional Experience</h3>
          <Card className="rounded-xl shadow-sm p-6">
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-muted-foreground">
                <div>State Bank of India</div>
                <div>Bank of Baroda</div>
                <div>Punjab National Bank</div>
                <div>UCO Bank</div>
                <div>Indian Bank</div>
                <div>Federal Bank</div>
                <div>Saurashtra Gramin Bank</div>
                <div>Shri Bhavnagar Nagrik Sahkari Bank</div>
                <div>Various Cooperative Banks, NBFCs and Corporate Institutions</div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-8" />

        {/* Our Expertise */}
        <section className="mb-8">
          <h3 className="text-xl font-semibold text-blue-700 mb-4">Our Expertise</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="rounded-xl shadow-sm p-6">
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li>Immovable Property (Land & Building)</li>
                  <li>Agricultural Land</li>
                  <li>Residential Properties</li>
                  <li>Commercial Properties</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="rounded-xl shadow-sm p-6">
              <CardContent>
                <ul className="space-y-2 text-muted-foreground">
                  <li>Industrial Units & Factories</li>
                  <li>Government & Institutional Properties</li>
                  <li>Bank Loan & Mortgage Valuations</li>
                  <li>Valuation Reports in Compliance with Banking & Statutory Standards</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator className="my-8" />

        {/* Professional Commitment */}
        <section className="mb-12">
          <div className="border-l-4 border-blue-600 bg-white p-6 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-700">Our Professional Commitment</h4>
            <p className="mt-2 text-muted-foreground">We follow independence, integrity, transparency, objectivity and full compliance with banking and statutory regulations in every assignment.</p>
          </div>
        </section>

        {/* CTA to Major Assignments */}
        <div className="text-center">
          {/* <Link to="/major-assignments">
            <Button variant="default">View Major Assignments & Valuation Projects</Button>
          </Link> */}
        </div>
      </div>
    </div>
  )
}
